/**
 * Shared Question copy for the detail card and the Thread tab seed — one source
 * of truth so the card body always mirrors as the first thread row (aims-htl).
 */

import { initialsFromName } from './initials.js';
import { formatMessageDateTime } from './detailFormat.js';

function readQuestionText(source) {
  const q = source?.question;
  if (typeof q === 'string' && q.trim()) return q.trim();
  if (q && typeof q === 'object') {
    const text = q.text ?? q.question;
    if (typeof text === 'string' && text.trim()) return text.trim();
  }
  return null;
}

/** Question prompt shown on the card (direct ask). */
export function resolveQuestionPrompt(event) {
  const detail = event?.detail ?? {};
  return (
    readQuestionText(detail) ??
    readQuestionText(detail.render) ??
    (typeof event?.description === 'string' ? event.description.trim() : null) ??
    null
  );
}

/** Broadcast MESSAGE body (distinct from the explicit ask line). */
export function resolveQuestionMessageBody(event) {
  const detail = event?.detail ?? {};
  const isBroadcast = detail.questionVariant === 'broadcast';
  if (!isBroadcast) return null;
  const prompt = resolveQuestionPrompt(event);
  return (
    detail.messageBody ??
    event?.situation ??
    event?.description ??
    prompt ??
    null
  );
}

export function resolveQuestionAsker(event) {
  const detail = event?.detail ?? {};
  if (detail.askedBy?.name) {
    return {
      ...detail.askedBy,
      initials: detail.askedBy.initials || initialsFromName(detail.askedBy.name),
    };
  }

  const render = detail.render ?? {};
  const raw = render.question;
  if (raw && typeof raw === 'object') {
    const name = raw.from ?? raw.name ?? 'Unknown';
    return {
      id: raw.id ?? name,
      name,
      role: raw.role ?? '',
      initials: raw.initials ?? initialsFromName(name),
    };
  }

  const name = render.question_from ?? render.askedBy?.name ?? render.asked_by?.name;
  if (name) {
    return {
      id: name,
      name,
      role: render.question_role ?? '',
      initials: initialsFromName(name),
    };
  }

  return null;
}

/** First thread row body — always the same text the card shows. */
export function resolveQuestionThreadBody(event) {
  const detail = event?.detail ?? {};
  const isBroadcast = detail.questionVariant === 'broadcast';
  if (isBroadcast) return resolveQuestionMessageBody(event);
  return resolveQuestionPrompt(event);
}

export function resolveQuestionDisplayTime(event, locale) {
  const detail = event?.detail ?? {};
  return formatMessageDateTime(detail.askedAt ?? detail.createdAt, locale) ?? '';
}

export function buildQuestionThreadSeed(event) {
  const mirrorBody = resolveQuestionThreadBody(event);
  const asker = resolveQuestionAsker(event);
  const displayAt = resolveQuestionDisplayTime(event);

  if (!mirrorBody) {
    return { status: 'open', comments: [] };
  }

  return {
    status: 'open',
    comments: [
      {
        id: `seed-message-${event.id}`,
        author: asker?.name ?? 'Unknown',
        role: asker?.role ?? '',
        initials: asker?.initials ?? initialsFromName(asker?.name),
        timeAgo: displayAt,
        body: mirrorBody,
        kind: 'message',
      },
    ],
  };
}
